import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  inject,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  merge,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { collections } from 'src/app/core/constants/collections';
import { constants } from 'src/app/core/constants/constants';
import { dataTableEvents } from 'src/app/core/constants/data-table-events';
import { PermissionResource } from 'src/app/core/constants/permissions.constants';
import {
  BaseEventsService,
  Criterion,
  EventsServiceAction,
  EventsServiceData,
  SearchItemModel,
} from 'src/app/core/services/base-event-service';
import { eventServicesList } from 'src/app/core/services/event-services-list';
import {
  FILTER_OPERATOR,
  SearchCriterionsDataType,
} from 'src/app/core/services/client-side-search.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { selectDefaultReportTypeId, selectUser } from 'src/app/core/store/selectors';
import { AppState } from 'src/app/core/store/state';
import { markAsNotRequired, markAsRequired } from 'src/app/core/utils/form-controls.utils';
import { isPresent } from 'src/app/core/utils/isPresent';
import { emailListValidator } from 'src/app/core/validators/email-list-validator';
import {
  PartiesApiService,
  PartyDto,
} from 'src/app/modules/counterparties/services/parties-api.service';
import { CounterpartiesActions } from 'src/app/modules/counterparties/store/actions';
import { GetDocumentTypeDto } from 'src/app/modules/doc-types/models/doc-types.model';
import { DocumentTypesApiService } from 'src/app/modules/doc-types/services/document-types-api.service';
import { DocTypesActions } from 'src/app/modules/doc-types/store/actions';
import { UserDto } from 'src/app/modules/login/services/user-api.service';
import { PlaybookDto } from 'src/app/modules/playbooks/models/playbooks.model';
import { PlaybooksApiService } from 'src/app/modules/playbooks/services/playbooks-api.service';
import { DialogsActions } from 'src/app/modules/dialogs/store/actions';
import { NotificationsActions } from 'src/app/modules/notifications/store/actions';
import { DataTableItem } from 'src/app/shared/components/data-table/client-side-data-table/client-side-data-table.component';
import { ThreeDotsMenuPipe } from 'src/app/shared/pipes/three-dots-menu.pipe';
import {
  DocumentSignatureApiService,
  DocumentSignatureAuthUrlResponse,
  DocumentSignatureRecipientRole,
  DocumentSignatureSession,
  DocumentSignatureSessionRecipient,
  DocumentSignatureSessionRecipientStatus,
  DocumentSignatureSessionStatus,
  PrepareDocumentSignatureRequest,
} from 'src/app/modules/document-signature/services/document-signature-api.service';
import {
  RecipientDto,
  RecipientsApiService,
} from 'src/app/modules/recipients/services/recipients-api.service';
import { LanguageDto } from 'src/app/shared/services/languages-api.service';
import { ValidationErrorsService } from 'src/app/shared/services/validation-errors.service';
import { DocumentStatusesApiService } from 'src/app/shared/services/document-statuses-api.service';
import {
  FileUploadApiService,
  FileUploadResultDto,
} from 'src/app/shared/services/file-upload.api-service';
import { PositionDto, PositionsApiService } from 'src/app/shared/services/positions-api.service';
import { ReportFormat, SendReportDto } from 'src/app/shared/services/reporting-api.service';
import { AppButtonSize, AppButtonType } from 'src/app/shared/shared.model';
import { selectContentLanguages } from 'src/app/shared/store/selectors';
import { getFilenameFromContentDisposition } from 'src/app/shared/utils';
import { environment } from 'src/environment/environment';
import {
  DocumentContentType,
  DocumentDetailsField,
  DocumentDetailsTriggerModel,
  DocumentVersionDto,
  DocumentPropertiesDto,
  GetDocumentDto,
  GetDocumentVersionDto,
} from '../../models/documents.model';
import { DocumentsApiService } from '../../services/documents-api.service';
import { DocumentsActions } from '../../store/actions';
import { fromDocuments } from '../../store/selectors';
import { DocumentPageReviewProgress } from '../../store/state';
import {
  buildDocumentDetailsPlaybookOptions,
  buildPlaybookComplianceReview,
  buildPlaybookNameContext,
  cloneDocument,
  getComplianceStatusClass,
  getComplianceStatusLabelKey,
  getFlagClass,
  getNumericValue,
  getReportContents,
  getSelectedCounterpartyIds,
  getSuggestedReportPlaybooks,
  hasPersistedDocumentChanged,
  isKnownPlaybookId,
  reportContentValidator,
  resolveInitialReportPlaybookId,
} from './document-details-drawer.helper';
import {
  FlagType,
} from '../../services/document-processing-api.service';
import {
  PlaybookComplianceReviewSummary,
  PlaybookOption,
} from './document-details-drawer.models';

const DRAWER_OPEN_TRANSITION_MS = 220;
const playbookComplianceFlagFilters = [
  FlagType.MostDesired,
  FlagType.Fallback,
  FlagType.Unacceptable,
];

enum DocumentVersionAction {
  Open = 'open',
  OpenInWordDesktop = 'openInWordDesktop',
  Download = 'download',
  Remove = 'remove',
}

type DocumentVersionTableRow = DocumentVersionDto & {
  versionKey: string;
  searchText: string;
};

enum DocumentVersionSearchCriterion {
  Search = 1,
}

interface DocumentSignatureRecipient {
  id: number;
  nameControl: FormControl;
  emailControl: FormControl;
  roleControl: FormControl;
  completedAt: string | null;
  resolvedRecipient: RecipientDto | null;
  autocompleteOptions: RecipientDto[];
  isAutocompleteLoading: boolean;
  destroy$: Subject<void>;
}

interface SignatureRecipientSubmission {
  recipient: DocumentSignatureRecipient;
  recipientId: string | null;
  name: string;
  email: string;
  role: DocumentSignatureRecipientRole;
}

@UntilDestroy()
@Component({
  selector: 'app-document-details-drawer',
  templateUrl: './document-details-drawer.component.html',
  styleUrl: './document-details-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentDetailsDrawerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isOpen: boolean = false;
  @Input() public detailsTrigger: DocumentDetailsTriggerModel | null = null;
  @Output() public closed = new EventEmitter<void>();

  public form: FormGroup | undefined;
  public addDocTypeForm: FormGroup | undefined;
  public addPartyForm: FormGroup | undefined;
  public reportForm: FormGroup | undefined;
  public btnTypes = AppButtonType;
  public btnSizes = AppButtonSize;
  public constants = constants;
  public focusFields = DocumentDetailsField;
  public isPropertiesExpanded: boolean = true;
  public isVersionsExpanded: boolean = false;
  public isPlaybookComplianceExpanded: boolean = true;
  public isReportExpanded: boolean = false;
  public isSignatureRequestExpanded: boolean = false;
  public reportFormats = collections.reportFormats;
  public versionSearchControl = new FormControl('');
  public versionColumns!: DataTables.ColumnSettings[];
  public versionSort!: (string | number)[][];
  public versionMenuItems: DataTableItem[] = [];
  public versionTableRows: DocumentVersionTableRow[] = [];
  public readonly versionsTableId = 'document-details-drawer-versions-table-id';
  public refreshVersionsTable: BehaviorSubject<any> = new BehaviorSubject(null);
  public readonly documentDetailsDrawerVersionsEventsService = new BaseEventsService(
    eventServicesList.documentVersionsList,
  );
  public readonly maxDocusignRecipients = environment.documentSignature.providers.docusign.maxRecipients;

  public docTypeItems: GetDocumentTypeDto[] = [];
  public docStatusItems: any;
  public positionItems: PositionDto[] = [];
  public conterPartyItems: PartyDto[] = [];
  public hiddenConterPartyItems: number[] = [];
  public playbookItems: PlaybookOption[] = [];
  public creatingNewDocType: boolean = false;
  public creatingNewParty: boolean = false;
  public currentUser: UserDto | null | undefined;
  public isDocumentLoading$ = this.store$.select(fromDocuments.selectDocumentLoading);
  public isDocumentEditing$ = this.store$.select(fromDocuments.selectDocumentEditing);
  public isSendingReport$ = this.store$.select(fromDocuments.selectSendingReport);
  public document: GetDocumentDto | null | undefined;
  public playbookComplianceReview: PlaybookComplianceReviewSummary | null = null;
  public selectedPlaybookComplianceFlagTypes = new Set<FlagType>(playbookComplianceFlagFilters);
  public contentLanguages: LanguageDto[] = [];
  public activeReviewDocumentUniqueId: string | null = null;
  public reviewingDocumentsProgressById: Record<string, DocumentPageReviewProgress> = {};
  public readonly playbookComplianceSkeletonRows = [1, 2, 3];
  public versions: DocumentVersionDto[] = [];
  public versionsLoading: boolean = false;
  public versionUploadInProgress: boolean = false;
  public currentVersionNumber: number | null = null;
  public readonly versionActions = DocumentVersionAction;
  public readonly acceptedVersionFileTypes = constants.fileUploaderAcceptedFormats.join(',');
  public signatureRecipientRoleOptions: { id: DocumentSignatureRecipientRole; name: string }[] = [];
  public signatureRecipients: DocumentSignatureRecipient[] = [];
  public isPreparingSignatureRequest: boolean = false;
  public isSignatureRequestPending: boolean = false;
  public isSignatureRequestCompleted: boolean = false;
  public signatureSessionStatus: DocumentSignatureSessionStatus | null = null;
  public docusignUrl: SafeResourceUrl | null = null;
  public canWriteDocuments = false;
  public canReadReporting = false;
  public canWriteReporting = false;
  private canWriteCounterparties = false;

  private signatureSessionId: string | null = null;
  private loadedDocumentId: string | null = null;
  private lastSavedDocument: GetDocumentDto | null = null;
  private pendingFocusField: DocumentDetailsField | undefined;
  private waitForDrawerSettleBeforeFocus: boolean = false;
  private reportFormDocumentId: string | null = null;
  private forceVersionContextRefresh: boolean = false;
  private readonly formsReset$ = new Subject<void>();
  private readonly versionSearchCriterions: Criterion[] = [
    {
      id: DocumentVersionSearchCriterion.Search,
      title: 'Search',
      data: ['searchText'],
      type: SearchCriterionsDataType.String,
    },
  ];
  private readonly signatureSessionPollingStop$ = new Subject<void>();
  private readonly docusignMinimumViewportWidth = 768;
  private readonly docusignReturnPathname = '/assets/docusign-return.html';
  private readonly docusignReturnUrl = `${environment.appBaseUrl}${this.docusignReturnPathname}`;
  private readonly docusignBodyClassName = 'docusign-active';
  private nextSignatureRecipientId = 1;
  private readonly recipientsApiService = inject(RecipientsApiService);

  constructor(
    private readonly store$: Store<AppState>,
    private readonly elementRef: ElementRef,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly documentTypesApiService: DocumentTypesApiService,
    private readonly documentStatusesApiService: DocumentStatusesApiService,
    private readonly positionsApiService: PositionsApiService,
    private readonly partiesApiService: PartiesApiService,
    private readonly playbooksApiService: PlaybooksApiService,
    private readonly documentsApiService: DocumentsApiService,
    private readonly fileUploadApiService: FileUploadApiService,
    private readonly translocoService: TranslocoService,
    public readonly threeDotsMenuPipe: ThreeDotsMenuPipe,
    private readonly documentSignatureApiService: DocumentSignatureApiService,
    private readonly sanitizer: DomSanitizer,
    private readonly validationErrorsService: ValidationErrorsService,
    private readonly permissionService: PermissionService,
  ) {}

  ngOnDestroy(): void {
    this.stopSignatureSessionPolling();
    this.destroyDocusignIframe();
  }

  @HostListener('window:message', ['$event'])
  onDocusignMessage(event: MessageEvent): void {
    if (!this.isDocusignCompleteMessage(event.data)) {
      return;
    }

    this.completeDocusignRequest();
  }

  ngOnInit(): void {
    this.documentDetailsDrawerVersionsEventsService.setFilterCriterions(
      this.versionSearchCriterions,
    );
    this.documentDetailsDrawerVersionsEventsService.dataWasLoaded = false;
    this.initVersionsTable();
    this.initVersionsSubscriptions();
    this.signatureRecipientRoleOptions = this.buildSignatureRecipientRoleOptions();
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.signatureRecipientRoleOptions = this.buildSignatureRecipientRoleOptions();
      this.cdr.markForCheck();
    });

    combineLatest([
      this.store$.select(selectUser),
      this.store$.select(selectDefaultReportTypeId),
      this.store$.select(fromDocuments.selectDocument).pipe(filter(isPresent)),
      this.store$.select(selectContentLanguages).pipe(filter(isPresent)),
      this.documentTypesApiService.getDocumentTypes$(),
      this.documentStatusesApiService.getDocumentStatuses$(),
      this.positionsApiService.getPositions$(),
      this.partiesApiService.getParties$(),
      this.playbooksApiService.getPlaybooks$().pipe(catchError(() => of([] as PlaybookDto[]))),
      this.store$.select(fromDocuments.selectActivePageReviewDocumentUniqueId),
      this.store$.select(fromDocuments.selectPageReviewProgressByDocumentId),
      this.store$.select(fromDocuments.selectPageReviewResponseDataByDocumentId),
      this.permissionService.canWrite$(PermissionResource.Documents),
      this.permissionService.canWrite$(PermissionResource.Counterparties),
      this.permissionService.canRead$(PermissionResource.Reporting),
      this.permissionService.canWrite$(PermissionResource.Reporting),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(
        ([
          user,
          defaultReportTypeId,
          document,
          contentLanguages,
          docTypes,
          docStatuses,
          positions,
          parties,
          playbooks,
          activeReviewDocumentUniqueId,
          reviewingDocumentsProgressById,
          pageReviewResponseDataByDocumentId,
          canWriteDocuments,
          canWriteCounterparties,
          canReadReporting,
          canWriteReporting,
        ]) => {
          if (!this.isOpen || document.uniqueId !== this.detailsTrigger?.uniqueId) {
            return;
          }

          this.canWriteDocuments = canWriteDocuments;
          this.canWriteCounterparties = canWriteCounterparties;
          this.canReadReporting = canReadReporting;
          this.canWriteReporting = canWriteReporting;
          this.syncReportExpansionAccess();
          this.currentUser = user;
          this.document = document;
          this.contentLanguages = contentLanguages;
          this.docTypeItems = docTypes;
          this.docStatusItems = docStatuses;
          this.positionItems = positions;
          this.conterPartyItems = parties;
          this.hiddenConterPartyItems = [];
          this.activeReviewDocumentUniqueId = activeReviewDocumentUniqueId;
          this.reviewingDocumentsProgressById = reviewingDocumentsProgressById;
          this.playbookItems = buildDocumentDetailsPlaybookOptions(
            playbooks,
            buildPlaybookNameContext(
              this.docTypeItems,
              this.positionItems,
              this.contentLanguages,
              this.translocoService.translate('placeholders.select'),
              this.translocoService.translate('text.none'),
            ),
          );
          this.playbookComplianceReview = buildPlaybookComplianceReview(
            document,
            pageReviewResponseDataByDocumentId[document.uniqueId],
          );

          this.syncPersistedDocument(document, defaultReportTypeId ?? null);
          this.syncFormAccess();
        },
      );

    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe(() => {
      this.initVersionsTable();
      this.scheduleVersionsTableRefresh();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      const nextFocusField =
        changes['detailsTrigger']?.currentValue?.focusField || this.detailsTrigger?.focusField;

      this.waitForDrawerSettleBeforeFocus =
        !!changes['isOpen'].currentValue && !changes['isOpen'].previousValue && !!nextFocusField;
    }

    if (changes['detailsTrigger'] || changes['isOpen']) {
      this.handleTriggerChange();
    }
  }

  onDocusignIframeLoad(event: Event): void {
    const iframe = event.target as HTMLIFrameElement | null;
    const iframeLocation = iframe?.contentWindow?.location;

    if (!iframeLocation) {
      return;
    }

    try {
      if (iframeLocation.origin !== window.location.origin) {
        return;
      }

      if (iframeLocation.pathname !== this.docusignReturnPathname) {
        return;
      }

      this.completeDocusignRequest();
    } catch {
      // Ignore cross-origin loads until DocuSign returns to our callback page.
    }
  }

  public openNewDocTypeForm(): void {
    if (!this.canWriteDocuments) {
      return;
    }

    this.creatingNewDocType = true;
  }

  public onConfirmNewDocType(): void {
    if (!this.canWriteDocuments) {
      return;
    }

    this.documentTypesApiService
      .createDocumentType$(this.addDocTypeForm?.get('newDocumentType')?.value)
      .pipe(
        untilDestroyed(this),
        catchError(() => {
          this.addDocTypeForm?.get('newDocumentType')?.setErrors({ docTypeNameAlreadyExist: true });
          const invalidControl = this.elementRef.nativeElement.querySelector('#newDocumentType');
          invalidControl?.focus();
          return of();
        }),
      )
      .subscribe((newDocType: GetDocumentTypeDto) => {
        if (!newDocType) {
          this.onCancelNewDocType();
          return;
        }

        this.documentTypesApiService
          .getDocumentTypes$()
          .pipe(untilDestroyed(this))
          .subscribe((docTypeItems) => {
            this.store$.dispatch(DocTypesActions.setDocTypes({ docTypes: docTypeItems }));
            this.docTypeItems = docTypeItems;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.form?.get('documentType')?.patchValue(newDocType.id);
              this.form?.updateValueAndValidity();
              this.onCancelNewDocType();
              this.cdr.detectChanges();
            });
          });
      });
  }

  public onCancelNewDocType(): void {
    this.addDocTypeForm?.reset();
    this.creatingNewDocType = false;
    this.cdr.markForCheck();
  }

  public openNewPartyForm(): void {
    if (!this.canCreateNewCounterparty) {
      return;
    }

    this.creatingNewParty = true;
  }

  public onConfirmNewParty(): void {
    if (!this.canCreateNewCounterparty) {
      return;
    }

    this.partiesApiService
      .createParty$(this.addPartyForm?.get('newPartyName')?.value)
      .pipe(
        untilDestroyed(this),
        catchError(() => {
          this.addPartyForm?.get('newPartyName')?.setErrors({ partyNameAlreadyExist: true });
          const invalidControl = this.elementRef.nativeElement.querySelector('#newPartyName');
          invalidControl?.focus();
          return of();
        }),
      )
      .subscribe((response: PartyDto) => {
        if (!response) {
          this.creatingNewParty = false;
          return;
        }

        this.store$.dispatch(CounterpartiesActions.getCounterparties());

        this.partiesApiService
          .getParties$()
          .pipe(untilDestroyed(this))
          .subscribe((parties) => {
            this.conterPartyItems = parties;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.form?.get('counterparties')?.patchValue([response.id]);
              this.form?.updateValueAndValidity();
              this.onCancelNewParty();
              this.creatingNewParty = false;
              this.cdr.detectChanges();
            });
          });
      });
  }

  public onCancelNewParty(): void {
    this.addPartyForm?.reset();
    this.creatingNewParty = false;
    this.cdr.markForCheck();
  }

  public get canCreateNewCounterparty(): boolean {
    return this.canWriteDocuments && this.canWriteCounterparties;
  }

  public onConfirm(): void {
    if (!this.canWriteDocuments) {
      return;
    }

    const properties = {
      name: this.form?.get('documentName')?.value,
      type: getNumericValue(this.form?.get('documentType')?.value) || 0,
      userPositionId: getNumericValue(this.form?.get('myPosition')?.value) || 0,
      parties: getSelectedCounterpartyIds(this.form?.get('counterparties')?.value),
      status: getNumericValue(this.form?.get('status')?.value) || 0,
      languageId: getNumericValue(this.form?.get('languageId')?.value) || 0,
      revisionOption: this.document?.revisionOption || this.currentUser?.defaultRevisionOption,
    } as DocumentPropertiesDto;

    this.store$.dispatch(
      DocumentsActions.editDocument({
        model: {
          assignedPlaybookId: this.form?.get('assignedPlaybookId')?.value || undefined,
          properties,
        },
        closeOnSuccess: false,
        uniqueId: this.detailsTrigger?.uniqueId || '',
      }),
    );
  }

  public onCancel(): void {
    this.restoreLastSavedState();
  }

  public onDrawerClose(): void {
    this.stopSignatureSessionPolling();
    this.destroyDocusignIframe();
    this.closed.emit();
  }

  public toggleProperties(): void {
    this.isPropertiesExpanded = !this.isPropertiesExpanded;
  }

  public togglePlaybookCompliance(): void {
    this.isPlaybookComplianceExpanded = !this.isPlaybookComplianceExpanded;
  }

  public startPlaybookComplianceReview(): void {
    if (
      !this.document ||
      !this.canStartPlaybookComplianceReview ||
      !this.selectedCompliancePlaybookId
    ) {
      return;
    }

    this.store$.dispatch(
      DocumentsActions.startPageReview({
        documentUniqueId: this.document.uniqueId,
        assignedPlaybookId: this.selectedCompliancePlaybookId,
      }),
    );
  }

  public toggleVersions(): void {
    this.isVersionsExpanded = !this.isVersionsExpanded;

    if (this.isVersionsExpanded) {
      this.scheduleVersionsTableRefresh();
    }
  }

  public toggleReport(): void {
    this.isReportExpanded = !this.isReportExpanded;
  }

  public openVersionUploadPicker(fileInput: HTMLInputElement): void {
    if (this.versionUploadInProgress) {
      return;
    }

    fileInput.click();
  }

  public onVersionFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = '';

    if (!file || !this.detailsTrigger?.uniqueId) {
      return;
    }

    if (!this.isAcceptedVersionFile(file)) {
      this.showVersionErrorNotification(
        this.translocoService.translate('errors.wrongFileTypeDocxPdf'),
      );
      return;
    }

    this.uploadVersion(this.detailsTrigger.uniqueId, file);
  }

  public toggleSignatureRequest(): void {
    this.isSignatureRequestExpanded = !this.isSignatureRequestExpanded;
  }

  public addSignatureRecipient(): void {
    if (!this.canAddSignatureRecipient || this.isSignatureRequestPending) {
      return;
    }

    this.signatureRecipients = [...this.signatureRecipients, this.createSignatureRecipient()];
    this.cdr.markForCheck();
  }

  public removeSignatureRecipient(recipientId: number): void {
    if (this.isPreparingSignatureRequest || this.isSignatureRequestPending) {
      return;
    }

    this.signatureRecipients.find((recipient) => recipient.id === recipientId)?.destroy$.next();

    const remainingRecipients = this.signatureRecipients.filter(
      (recipient) => recipient.id !== recipientId,
    );

    this.signatureRecipients = remainingRecipients.length
      ? remainingRecipients
      : [this.createSignatureRecipient()];
    this.cdr.markForCheck();
  }

  public onSendForSignatureConfirm(): void {
    if (!this.document || !this.isSendForSignatureEnabled) {
      return;
    }

    const documentUniqueId = this.document.uniqueId;

    if (!this.isDocusignViewportSupported()) {
      this.showNotification(
        this.translocoService.translate('text.documentPreparationUnavailableTitle'),
        this.translocoService.translate('text.documentPreparationRequiresLargerScreen'),
      );
      return;
    }

    this.isPreparingSignatureRequest = true;
    this.showNotification(
      this.translocoService.translate('text.preparingSignatureRequestTitle'),
      this.translocoService.translate('text.preparingSignatureRequestText'),
      constants.icons.signature,
    );

    this.buildPrepareDocumentSignatureRequest$()
      .pipe(
        switchMap((payload) =>
          this.documentSignatureApiService.prepareDocument$(documentUniqueId, payload),
        ),
        catchError((error: HttpErrorResponse | Error) => {
          this.isPreparingSignatureRequest = false;
          this.showNotification(
            error instanceof HttpErrorResponse
              ? this.getDocumentPreparationErrorTitle(error)
              : this.translocoService.translate('text.documentPreparationUnavailableTitle'),
            '',
            constants.icons.redCross,
          );

          return EMPTY;
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (response) => {
          this.isPreparingSignatureRequest = false;
          this.isSignatureRequestPending = true;
          this.cdr.markForCheck();

          if (!response.url) {
            return;
          }

          this.renderDocusignIframe(this.getDocusignIframeUrl(response.url));
        },
      });
  }

  public onVersionAction(action: DocumentVersionAction, version: DocumentVersionDto): void {
    switch (action) {
      case DocumentVersionAction.Open: {
        this.openVersion(version);
        break;
      }
      case DocumentVersionAction.OpenInWordDesktop: {
        this.openVersionInWordDesktop(version);
        break;
      }
      case DocumentVersionAction.Download: {
        this.downloadVersion(version);
        break;
      }
      case DocumentVersionAction.Remove: {
        this.confirmDeleteVersion(version);
        break;
      }
    }
  }

  public onCancelSignatureRequest(): void {
    if (!this.document || !this.isSignatureRequestPending) {
      return;
    }

    this.documentSignatureApiService
      .cancelSignatureSession$(this.document.uniqueId, {
        reason: this.translocoService.translate('tooltip.cancelSigning'),
      })
      .pipe(
        catchError(() => EMPTY),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.destroyDocusignIframe();
        this.clearSignatureRequestState();
      });
  }

  public downloadSignedDocument(): void {
    if (!this.document || !this.isSignatureRequestCompleted) {
      return;
    }

    this.documentSignatureApiService
      .downloadSignedDocument$(this.document.uniqueId)
      .pipe(
        catchError(() => EMPTY),
        untilDestroyed(this),
      )
      .subscribe((response) => {
        const blob = new Blob([response.body!], { type: response.body!.type });
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = getFilenameFromContentDisposition(contentDisposition) || this.document!.name;
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = filename;
        anchor.click();
        window.URL.revokeObjectURL(url);
      });
  }

  public onSendReportConfirm(): void {
    if (!this.canWriteReporting) {
      return;
    }

    if (!this.document || !this.reportForm) {
      return;
    }

    const recipients: string[] = [];

    if (this.reportForm.get('sendToMe')?.value === true && this.currentUser?.email) {
      recipients.push(this.currentUser.email);
    }

    if (this.reportForm.get('sendToOthers')?.value === true) {
      recipients.push(
        ...`${this.reportForm.get('sendToOthersEmails')?.value || ''}`
          .split(',')
          .map((email: string) => email.trim())
          .filter(Boolean),
      );
    }

    const reportContents = getReportContents(this.reportForm);

    const report: SendReportDto = {
      documentId: this.document.uniqueId,
      recipients: recipients as [string],
      reportFormatId: this.reportForm.get('reportFormat')?.value,
      reportContents,
    };

    const selectedPlaybookId = this.reportForm.get('selectedPlaybookId')?.value;

    if (selectedPlaybookId) {
      report.playbookId = selectedPlaybookId;
    }

    this.store$.dispatch(DocumentsActions.sendReport({ report, doc: this.document }));
  }

  public get isSendToOthers(): boolean {
    return this.reportForm?.get('sendToOthers')?.value === true;
  }

  public get isSendToOthersEmailsInvalid(): boolean {
    const control = this.reportForm?.get('sendToOthersEmails');
    return !!control?.invalid && !!(control.dirty || control.touched);
  }

  public get isSendReportFormValid(): boolean {
    if (!this.reportForm || this.reportForm.invalid) {
      return false;
    }

    if (this.isPlaybookAnalysisSelected && !this.hasSelectedReportPlaybook) {
      return false;
    }

    return (
      this.reportForm.get('sendToMe')?.value === true ||
      this.reportForm.get('sendToOthers')?.value === true
    );
  }

  public get hasSelectedReportPlaybook(): boolean {
    return !!this.reportForm?.get('selectedPlaybookId')?.value;
  }

  public get isPlaybookAnalysisSelected(): boolean {
    return this.reportForm?.get('reportContentPlaybookAnalysis')?.value === true;
  }

  public get reportPlaybookOptions(): PlaybookOption[] {
    return this.suggestedReportPlaybooks.length
      ? this.suggestedReportPlaybooks
      : this.playbookItems;
  }

  public get selectedReportPlaybookLabel(): string {
    const selectedPlaybookId = this.reportForm?.get('selectedPlaybookId')?.value;

    return (
      this.playbookItems.find((playbook) => playbook.uniqueId === selectedPlaybookId)
        ?.displayName || ''
    );
  }

  public get drawerTitle(): string {
    return this.document?.name || this.detailsTrigger?.documentName || '';
  }

  public get assignedPlaybookIdControl(): FormControl | null {
    return (this.form?.get('assignedPlaybookId') as FormControl | null) || null;
  }

  public get selectedCompliancePlaybookId(): string | null {
    const assignedPlaybookId = this.form?.get('assignedPlaybookId')?.value;

    if (typeof assignedPlaybookId === 'string' && assignedPlaybookId) {
      return assignedPlaybookId;
    }

    return this.document ? resolveInitialReportPlaybookId(this.document, this.playbookItems) : null;
  }

  public get hasSelectedCompliancePlaybook(): boolean {
    return !!this.selectedCompliancePlaybookId;
  }

  public get isPlaybookComplianceReviewInProgress(): boolean {
    return !!this.document && !!this.reviewingDocumentsProgressById[this.document.uniqueId];
  }

  public get playbookComplianceReviewProgress(): number | null {
    if (!this.document) {
      return null;
    }

    const progress = this.reviewingDocumentsProgressById[this.document.uniqueId];

    if (!progress) {
      return null;
    }

    if (!progress.totalPages) {
      return 0;
    }

    return Math.min(
      100,
      Math.max(0, Math.round((progress.pageNumber / progress.totalPages) * 100)),
    );
  }

  public get canStartPlaybookComplianceReview(): boolean {
    return !!this.selectedCompliancePlaybookId && !this.activeReviewDocumentUniqueId;
  }

  public get showPlaybookSelectionPrompt(): boolean {
    return (
      !this.playbookComplianceReview &&
      !this.isPlaybookComplianceReviewInProgress &&
      !this.hasSelectedCompliancePlaybook
    );
  }

  public get showPlaybookComplianceReviewButton(): boolean {
    return !this.playbookComplianceReview && !this.isPlaybookComplianceReviewInProgress;
  }

  public get filteredPlaybookComplianceItems() {
    return (
      this.playbookComplianceReview?.items.filter((item) =>
        this.isPlaybookComplianceFlagTypeSelected(item.flagType),
      ) || []
    );
  }

  public togglePlaybookComplianceFlagType(flagType: FlagType): void {
    const nextSelectedFlagTypes = new Set(this.selectedPlaybookComplianceFlagTypes);

    if (nextSelectedFlagTypes.has(flagType)) {
      nextSelectedFlagTypes.delete(flagType);
    } else {
      nextSelectedFlagTypes.add(flagType);
    }

    this.selectedPlaybookComplianceFlagTypes = nextSelectedFlagTypes;
  }

  public isPlaybookComplianceFlagTypeSelected(flagType: FlagType): boolean {
    if (flagType === FlagType.Missing) {
      return this.selectedPlaybookComplianceFlagTypes.has(FlagType.Unacceptable);
    }

    return this.selectedPlaybookComplianceFlagTypes.has(flagType);
  }

  public get flagTypes(): typeof FlagType {
    return FlagType;
  }

  public get canAddSignatureRecipient(): boolean {
    return this.signatureRecipients.length < this.maxDocusignRecipients;
  }

  public get isSendForSignatureEnabled(): boolean {
    return (
      !!this.document &&
      !this.isPreparingSignatureRequest &&
      !this.isSignatureRequestPending &&
      !this.signatureSessionStatus &&
      this.hasValidSignatureRecipients
    );
  }

  public get isSignatureRequestReadOnly(): boolean {
    return this.signatureSessionStatus !== null || this.isSignatureRequestPending;
  }

  public get signatureSessionStatusLabelKey(): string | null {
    switch (this.signatureSessionStatus) {
      case DocumentSignatureSessionStatus.Created:
        return 'labels.pendingSignatures';
      case DocumentSignatureSessionStatus.Completed:
        return 'labels.signed';
      case DocumentSignatureSessionStatus.Declined:
        return 'labels.signatureDeclined';
      case DocumentSignatureSessionStatus.Voided:
        return 'labels.signatureVoided';
      default:
        return null;
    }
  }

  public get hasValidSignatureRecipients(): boolean {
    return (
      this.signatureRecipients.length > 0 &&
      this.signatureRecipients.every(
        (recipient) =>
          recipient.nameControl.valid &&
          recipient.emailControl.valid &&
          recipient.roleControl.valid,
      )
    );
  }

  public getSignatureRecipientName(recipient: DocumentSignatureRecipient): string {
    return `${recipient.nameControl.value || ''}`.trim();
  }

  public getSignatureRecipientEmail(recipient: DocumentSignatureRecipient): string {
    return `${recipient.emailControl.value || ''}`.trim();
  }

  public getSignatureRecipientRoleLabel(recipient: DocumentSignatureRecipient): string {
    const role = recipient.roleControl.value as DocumentSignatureRecipientRole | null;

    return (
      this.signatureRecipientRoleOptions.find((option) => option.id === role)?.name ||
      this.translocoService.translate('text.none')
    );
  }

  public getSignatureRecipientAutocompleteOptions(
    recipient: DocumentSignatureRecipient,
    field: 'name' | 'email',
  ): RecipientDto[] {
    const value = field === 'name'
      ? this.getSignatureRecipientName(recipient)
      : this.getSignatureRecipientEmail(recipient);

    if (!value) {
      return recipient.autocompleteOptions;
    }

    const normalizedValue = this.normalizeSignatureRecipientValue(value);

    return recipient.autocompleteOptions.filter((option) =>
      this.normalizeSignatureRecipientValue(option[field]).includes(normalizedValue),
    );
  }

  public onSignatureRecipientOptionSelected(
    recipient: DocumentSignatureRecipient,
    option: RecipientDto,
  ): void {
    recipient.nameControl.patchValue(option.name, { emitEvent: false });
    recipient.emailControl.patchValue(option.email, { emitEvent: false });
    recipient.resolvedRecipient = option;
    recipient.autocompleteOptions = this.mergeSignatureRecipientAutocompleteOptions(
      recipient.autocompleteOptions,
      [option],
    );
    recipient.nameControl.markAsTouched();
    recipient.emailControl.markAsTouched();
    this.cdr.markForCheck();
  }

  public onSignatureRecipientAutocompleteValueSelected(
    recipient: DocumentSignatureRecipient,
    field: 'name' | 'email',
    value: string,
  ): void {
    const option = recipient.autocompleteOptions.find((item) => item[field] === value);

    if (!option) {
      return;
    }

    this.onSignatureRecipientOptionSelected(recipient, option);
  }

  public getSignatureRecipientControlErrorMessage(control: AbstractControl | null): string {
    if (!control?.errors) {
      return '';
    }

    return this.validationErrorsService.getControlErrorMessage(control.errors);
  }

  public isSignatureRecipientSubscriber(recipient: DocumentSignatureRecipient): boolean {
    return recipient.roleControl.value === DocumentSignatureRecipientRole.Signer;
  }

  public isSignatureRecipientCc(recipient: DocumentSignatureRecipient): boolean {
    return recipient.roleControl.value === DocumentSignatureRecipientRole.CarbonCopy;
  }

  public getSignatureRecipientStatusIcon(recipient: DocumentSignatureRecipient): string {
    if (recipient.completedAt) {
      return recipient.roleControl.value === DocumentSignatureRecipientRole.CarbonCopy
        ? 'mail'
        : 'task_alt';
    }

    return recipient.roleControl.value === DocumentSignatureRecipientRole.CarbonCopy
      ? 'forward_to_inbox'
      : 'edit_note';
  }

  public getSignatureRecipientStatusText(recipient: DocumentSignatureRecipient): string {
    if (recipient.completedAt) {
      const completedAt = this.formatSignatureCompletionDate(recipient.completedAt);

      return recipient.roleControl.value === DocumentSignatureRecipientRole.CarbonCopy
        ? this.translocoService.translate('text.signatureCopyReceivedOn', { date: completedAt })
        : this.translocoService.translate('text.signatureSignedOn', { date: completedAt });
    }

    return this.getSignatureRecipientRoleLabel(recipient);
  }

  public getFlagClass(flagType: FlagType): string {
    return getFlagClass(flagType);
  }

  public getComplianceStatusClass(flagType: FlagType): string {
    return getComplianceStatusClass(flagType);
  }

  public getComplianceStatusLabelKey(flagType: FlagType): string {
    return getComplianceStatusLabelKey(flagType);
  }

  public getVersionLabel(versionNumber: number): string {
    return `v${versionNumber}`;
  }

  public formatVersionDate(dateValue: string): string {
    if (!dateValue) {
      return '';
    }

    return new Intl.DateTimeFormat(this.translocoService.getActiveLang(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateValue));
  }

  public isVersionWordDesktopDisabled(version: DocumentVersionDto): boolean {
    const normalizedName = `${version.documentName || ''}`.trim().toLowerCase();

    return (
      (version as GetDocumentVersionDto).contentType === DocumentContentType.PDF ||
      normalizedName.endsWith('.pdf')
    );
  }

  private handleTriggerChange(): void {
    if (!this.isOpen || !this.detailsTrigger?.uniqueId) {
      this.form = undefined;
      this.reportForm = undefined;
      this.document = null;
      this.loadedDocumentId = null;
      this.lastSavedDocument = null;
      this.reportFormDocumentId = null;
      this.pendingFocusField = undefined;
      this.waitForDrawerSettleBeforeFocus = false;
      this.forceVersionContextRefresh = false;
      this.creatingNewDocType = false;
      this.creatingNewParty = false;
      this.resetVersionsState();
      this.isPreparingSignatureRequest = false;
      this.isSignatureRequestExpanded = false;
      this.resetSignatureRecipients();
      this.clearSignatureRequestState(false);
      this.destroyDocusignIframe();
      this.formsReset$.next();
      this.cdr.markForCheck();
      return;
    }

    this.isPropertiesExpanded =
      this.detailsTrigger.focusField !== DocumentDetailsField.Report &&
      this.detailsTrigger.focusField !== DocumentDetailsField.PlaybookCompliance &&
      this.detailsTrigger.focusField !== DocumentDetailsField.SignatureRequest;
    this.isPlaybookComplianceExpanded =
      this.detailsTrigger.focusField === DocumentDetailsField.PlaybookCompliance;
    this.isVersionsExpanded = false;
    this.isReportExpanded = this.detailsTrigger.focusField === DocumentDetailsField.Report;
    this.isSignatureRequestExpanded =
      this.detailsTrigger.focusField === DocumentDetailsField.SignatureRequest;
    this.pendingFocusField = this.detailsTrigger.focusField;

    if (this.loadedDocumentId === this.detailsTrigger.uniqueId) {
      this.focusRequestedField();
      return;
    }

    this.loadedDocumentId = this.detailsTrigger.uniqueId;
    this.form = undefined;
    this.reportForm = undefined;
    this.document = null;
    this.reportFormDocumentId = null;
    this.resetVersionsState(false);
    this.formsReset$.next();
    this.store$.dispatch(DocumentsActions.clearEditDocumentCancelled());
    this.store$.dispatch(DocumentsActions.loadDocument({ uniqueId: this.detailsTrigger.uniqueId }));
    this.loadVersions(this.detailsTrigger.uniqueId);
    this.cdr.markForCheck();
  }

  private buildForms(document: GetDocumentDto, defaultReportTypeId: number | null): void {
    this.formsReset$.next();

    this.form = this.fb.group({
      documentName: ['', { validators: [Validators.required] }],
      documentType: ['', { validators: [Validators.required] }],
      counterparties: ['', { validators: [Validators.required] }],
      myPosition: ['', { validators: [Validators.required] }],
      status: ['', { validators: [Validators.required] }],
      languageId: ['', { validators: [Validators.required] }],
      assignedPlaybookId: [null, { validators: [] }],
    });

    this.addDocTypeForm = this.fb.group({
      newDocumentType: ['', { validators: [Validators.required] }],
    });

    this.addPartyForm = this.fb.group({
      newPartyName: ['', { validators: [Validators.required] }],
    });

    this.reportForm = this.fb.group(
      {
        sendToMe: [true, { validators: [] }],
        sendToOthers: [false, { validators: [] }],
        sendToOthersEmails: ['', { validators: [emailListValidator] }],
        selectedPlaybookId: [null, { validators: [] }],
        reportContentSummary: [true, { validators: [] }],
        reportContentPlaybookAnalysis: [false, { validators: [] }],
        reportContentPlaybookCompliance: [{ value: false, disabled: true }, { validators: [] }],
        reportContentKeyQuestions: [{ value: false, disabled: true }, { validators: [] }],
        reportContentIntegrityCheck: [false, { validators: [] }],
        reportContentRegulatoryCompliance: [false, { validators: [] }],
        reportContentReadabilityAnalysis: [false, { validators: [] }],
        reportFormat: [defaultReportTypeId || ReportFormat.PDF, { validators: [] }],
      },
      { validators: [reportContentValidator] },
    );

    this.initReportFormSubscriptions();
    this.initDocumentContextSubscriptions();
    this.initAssignedPlaybookSubscription();
    this.applyDocumentToForm(document);
    this.applyDocumentToReportForm(document, defaultReportTypeId);
    this.resetSignatureRecipients();
    this.clearSignatureRequestState(false);
    this.loadSignatureSession(document.uniqueId);
  }

  private syncPersistedDocument(
    document: GetDocumentDto,
    defaultReportTypeId: number | null,
  ): void {
    this.document = document;

    if (this.forceVersionContextRefresh) {
      this.lastSavedDocument = cloneDocument(document);
      this.forceVersionContextRefresh = false;

      if (!this.form || !this.reportForm) {
        this.buildForms(document, defaultReportTypeId);
        return;
      }

      this.applyDocumentToForm(document, false);
      this.applyDocumentToReportForm(document, defaultReportTypeId);
      this.cdr.markForCheck();
      return;
    }

    if (!hasPersistedDocumentChanged(this.lastSavedDocument, document, this.playbookItems)) {
      this.syncReportFormState(document, defaultReportTypeId);
      this.cdr.markForCheck();
      return;
    }

    this.lastSavedDocument = cloneDocument(document);

    if (!this.form || !this.reportForm) {
      this.buildForms(document, defaultReportTypeId);
      return;
    }

    this.applyDocumentToForm(document, false);
    this.syncReportFormState(document, defaultReportTypeId);
  }

  private applyDocumentToForm(document: GetDocumentDto, restoreFocus: boolean = true): void {
    if (!this.form) {
      return;
    }

    this.form.reset(
      {
        documentName: document.name,
        documentType: document.type,
        counterparties: '',
        myPosition: document.userPositionId,
        status: document.status,
        languageId: document.languageId,
        assignedPlaybookId: resolveInitialReportPlaybookId(document, this.playbookItems),
      },
      { emitEvent: false },
    );

    setTimeout(() => {
      this.form?.get('counterparties')?.patchValue([...document.parties], { emitEvent: false });
      this.form?.markAsPristine();
      this.form?.markAsUntouched();

      if (restoreFocus) {
        this.focusRequestedField();
      }

      this.cdr.markForCheck();
    });

    this.cdr.markForCheck();
  }

  private applyDocumentToReportForm(
    document: GetDocumentDto,
    defaultReportTypeId: number | null,
  ): void {
    if (!this.reportForm) {
      return;
    }

    const initialReportPlaybookId = resolveInitialReportPlaybookId(document, this.playbookItems);

    this.reportFormDocumentId = document.uniqueId;
    this.reportForm.reset(
      {
        sendToMe: true,
        sendToOthers: false,
        sendToOthersEmails: '',
        selectedPlaybookId: initialReportPlaybookId,
        reportContentSummary: true,
        reportContentPlaybookAnalysis: !!initialReportPlaybookId,
        reportContentPlaybookCompliance: false,
        reportContentKeyQuestions: false,
        reportContentIntegrityCheck: false,
        reportContentRegulatoryCompliance: false,
        reportContentReadabilityAnalysis: false,
        reportFormat: defaultReportTypeId || ReportFormat.PDF,
      },
      { emitEvent: false },
    );

    this.syncPlaybookAnalysisControls();
    this.reportForm.markAsPristine();
    this.reportForm.markAsUntouched();
    this.cdr.markForCheck();
  }

  private syncReportFormState(document: GetDocumentDto, defaultReportTypeId: number | null): void {
    if (!this.reportForm) {
      return;
    }

    if (this.reportFormDocumentId !== document.uniqueId) {
      this.applyDocumentToReportForm(document, defaultReportTypeId);
      return;
    }

    const selectedPlaybookControl = this.reportForm.get('selectedPlaybookId');
    const selectedPlaybookId = selectedPlaybookControl?.value;

    if (selectedPlaybookId && !isKnownPlaybookId(selectedPlaybookId, this.playbookItems)) {
      selectedPlaybookControl?.patchValue(null, { emitEvent: false });
    }

    if (!selectedPlaybookId) {
      const resolvedPlaybookId = resolveInitialReportPlaybookId(document, this.playbookItems);

      if (resolvedPlaybookId) {
        selectedPlaybookControl?.patchValue(resolvedPlaybookId, { emitEvent: false });
      }
    }

    if (!this.reportForm.get('reportFormat')?.value && defaultReportTypeId) {
      this.reportForm.get('reportFormat')?.patchValue(defaultReportTypeId, { emitEvent: false });
    }

    this.syncPlaybookAnalysisControls();
  }

  private restoreLastSavedState(): void {
    if (!this.lastSavedDocument) {
      return;
    }

    this.onCancelNewDocType();
    this.onCancelNewParty();
    this.applyDocumentToForm(this.lastSavedDocument, false);
    this.store$.dispatch(
      DocumentsActions.setSelectedDocumentPlaybook({
        uniqueId: this.lastSavedDocument.uniqueId,
        assignedPlaybookId: resolveInitialReportPlaybookId(
          this.lastSavedDocument,
          this.playbookItems,
        ),
      }),
    );
  }

  private initReportFormSubscriptions(): void {
    this.reportForm
      ?.get('sendToOthers')
      ?.valueChanges.pipe(takeUntil(this.formsReset$), untilDestroyed(this), distinctUntilChanged())
      .subscribe((value) => {
        this.onSendToOthersChange(value === true);
      });

    this.reportForm
      ?.get('reportContentPlaybookAnalysis')
      ?.valueChanges.pipe(takeUntil(this.formsReset$), untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => {
        this.syncPlaybookAnalysisControls();
      });

    this.reportForm
      ?.get('selectedPlaybookId')
      ?.valueChanges.pipe(takeUntil(this.formsReset$), untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => {
        this.syncPlaybookAnalysisControls();
      });
  }

  private initDocumentContextSubscriptions(): void {
    merge(
      this.form?.get('documentType')?.valueChanges || of(null),
      this.form?.get('languageId')?.valueChanges || of(null),
      this.form?.get('myPosition')?.valueChanges || of(null),
    )
      .pipe(takeUntil(this.formsReset$), untilDestroyed(this), distinctUntilChanged())
      .subscribe(() => {
        this.reportForm?.get('selectedPlaybookId')?.patchValue(null, { emitEvent: false });
        this.syncPlaybookAnalysisControls();
        this.cdr.markForCheck();
      });
  }

  private initAssignedPlaybookSubscription(): void {
    this.form
      ?.get('assignedPlaybookId')
      ?.valueChanges.pipe(takeUntil(this.formsReset$), untilDestroyed(this), distinctUntilChanged())
      .subscribe((assignedPlaybookId) => {
        if (!this.document) {
          return;
        }

        this.store$.dispatch(
          DocumentsActions.setSelectedDocumentPlaybook({
            uniqueId: this.document.uniqueId,
            assignedPlaybookId: assignedPlaybookId || null,
          }),
        );
      });
  }

  private onSendToOthersChange(value: boolean): void {
    const sendToOthersEmailsControl = this.reportForm?.get(
      'sendToOthersEmails',
    ) as FormControl | null;

    if (!sendToOthersEmailsControl) {
      return;
    }

    if (value) {
      markAsRequired(sendToOthersEmailsControl);
    } else {
      sendToOthersEmailsControl.patchValue('', { emitEvent: false });
      markAsNotRequired(sendToOthersEmailsControl);
    }

    this.cdr.markForCheck();
  }

  private syncPlaybookAnalysisControls(): void {
    const playbookAnalysisControl = this.reportForm?.get('reportContentPlaybookAnalysis');
    const playbookComplianceControl = this.reportForm?.get('reportContentPlaybookCompliance');
    const keyQuestionsControl = this.reportForm?.get('reportContentKeyQuestions');

    if (!playbookAnalysisControl || !playbookComplianceControl || !keyQuestionsControl) {
      return;
    }

    if (playbookAnalysisControl.value === true && this.hasSelectedReportPlaybook) {
      playbookComplianceControl.enable({ emitEvent: false });
      keyQuestionsControl.enable({ emitEvent: false });
    } else {
      playbookComplianceControl.patchValue(false, { emitEvent: false });
      keyQuestionsControl.patchValue(false, { emitEvent: false });
      playbookComplianceControl.disable({ emitEvent: false });
      keyQuestionsControl.disable({ emitEvent: false });
    }

    this.reportForm?.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private syncFormAccess(): void {
    const documentForms = [this.form, this.addDocTypeForm, this.addPartyForm].filter(
      (form): form is FormGroup => !!form,
    );

    if (this.canWriteDocuments) {
      documentForms.forEach((form) => form.enable({ emitEvent: false }));
    } else {
      this.creatingNewDocType = false;
      this.creatingNewParty = false;
      documentForms.forEach((form) => form.disable({ emitEvent: false }));
    }

    if (this.canWriteReporting) {
      this.reportForm?.enable({ emitEvent: false });
      this.syncPlaybookAnalysisControls();
    } else {
      this.reportForm?.disable({ emitEvent: false });
    }

    this.cdr.markForCheck();
  }

  private syncReportExpansionAccess(): void {
    if (!this.canReadReporting && this.detailsTrigger?.focusField === DocumentDetailsField.Report) {
      this.isReportExpanded = false;
      this.isPropertiesExpanded = true;
    }
  }

  private get suggestedReportPlaybooks(): PlaybookOption[] {
    return getSuggestedReportPlaybooks(
      this.playbookItems,
      getNumericValue(this.form?.get('documentType')?.value),
      getNumericValue(this.form?.get('languageId')?.value),
      getNumericValue(this.form?.get('myPosition')?.value),
    );
  }

  private focusRequestedField(): void {
    const focusField = this.pendingFocusField;

    if (!focusField) {
      return;
    }

    this.isPropertiesExpanded =
      focusField !== DocumentDetailsField.Report &&
      focusField !== DocumentDetailsField.PlaybookCompliance &&
      focusField !== DocumentDetailsField.SignatureRequest;
    this.isPlaybookComplianceExpanded = focusField === DocumentDetailsField.PlaybookCompliance;
    this.isReportExpanded = focusField === DocumentDetailsField.Report;
    this.isSignatureRequestExpanded = focusField === DocumentDetailsField.SignatureRequest;

    const focusDelay = this.waitForDrawerSettleBeforeFocus ? DRAWER_OPEN_TRANSITION_MS : 0;
    this.waitForDrawerSettleBeforeFocus = false;

    setTimeout(() => {
      const fieldElement = this.elementRef.nativeElement.querySelector(
        `[data-focus-target="${focusField}"]`,
      ) as HTMLElement | null;

      fieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const fieldInputElement = fieldElement?.querySelector(
        'input, textarea, select',
      ) as HTMLElement | null;
      const fieldInteractiveElement = fieldElement?.querySelector(
        '[contenteditable="true"], [role="combobox"], [tabindex]:not([tabindex="-1"])',
      ) as HTMLElement | null;
      const fieldButtonElement = fieldElement?.querySelector('button') as HTMLElement | null;
      const focusableElement = fieldInputElement || fieldInteractiveElement || fieldButtonElement;

      requestAnimationFrame(() => {
        focusableElement?.focus();
      });
    }, focusDelay);
  }

  private loadVersions(documentUniqueId: string): void {
    this.versionsLoading = true;

    this.documentsApiService
      .getDocumentVersions$(documentUniqueId)
      .pipe(
        untilDestroyed(this),
        map((versions) =>
          [...(versions || [])].sort((left, right) => right.versionNumber - left.versionNumber),
        ),
        catchError(() => of([] as DocumentVersionDto[])),
        finalize(() => {
          this.versionsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe((versions) => {
        this.versions = versions;
        this.versionTableRows = this.buildVersionTableRows(versions);
        this.currentVersionNumber =
          versions.find((version) => version.isCurrentVersion)?.versionNumber ||
          versions[0]?.versionNumber ||
          null;
        this.refreshVersionsTableData();
        this.cdr.markForCheck();
      });
  }

  private createSignatureRecipient(
    signatureSessionRecipient?: DocumentSignatureSessionRecipient,
  ): DocumentSignatureRecipient {
    const recipient = {
      id: this.nextSignatureRecipientId,
      nameControl: this.fb.control(signatureSessionRecipient?.name || '', {
        validators: [Validators.required],
      }),
      emailControl: this.fb.control(signatureSessionRecipient?.email || '', {
        validators: [Validators.required, Validators.email],
      }),
      roleControl: this.fb.control(
        signatureSessionRecipient?.role ?? DocumentSignatureRecipientRole.Signer,
        {
          validators: [Validators.required],
        },
      ),
      completedAt:
        signatureSessionRecipient?.status === DocumentSignatureSessionRecipientStatus.Completed
          ? signatureSessionRecipient.statusChangedAt
          : null,
      resolvedRecipient: null,
      autocompleteOptions: [],
      isAutocompleteLoading: false,
      destroy$: new Subject<void>(),
    };

    this.initSignatureRecipientAutocomplete(recipient);

    this.nextSignatureRecipientId += 1;

    return recipient;
  }

  private resetSignatureRecipients(): void {
    this.signatureRecipients.forEach((recipient) => recipient.destroy$.next());
    this.nextSignatureRecipientId = 1;
    this.signatureRecipients = [this.createSignatureRecipient()];
  }

  private clearSignatureRequestState(resetRecipientStatuses: boolean = true): void {
    this.stopSignatureSessionPolling();
    this.isPreparingSignatureRequest = false;
    this.isSignatureRequestPending = false;
    this.isSignatureRequestCompleted = false;
    this.signatureSessionStatus = null;
    this.signatureSessionId = null;

    if (resetRecipientStatuses) {
      this.signatureRecipients = this.signatureRecipients.map((recipient) => ({
        ...recipient,
        completedAt: null,
      }));
    }

    this.cdr.markForCheck();
  }

  private buildSignatureRecipientRoleOptions(): {
    id: DocumentSignatureRecipientRole;
    name: string;
  }[] {
    return [
      {
        id: DocumentSignatureRecipientRole.Signer,
        name: this.translocoService.translate('labels.signatureRoleSigner'),
      },
      {
        id: DocumentSignatureRecipientRole.CarbonCopy,
        name: this.translocoService.translate('labels.signatureRoleCarbonCopy'),
      },
    ];
  }

  private initSignatureRecipientAutocomplete(recipient: DocumentSignatureRecipient): void {
    merge(recipient.nameControl.valueChanges, recipient.emailControl.valueChanges)
      .pipe(
        map(() => {
          this.syncResolvedSignatureRecipient(recipient);

          return this.getSignatureRecipientName(recipient) || this.getSignatureRecipientEmail(recipient);
        }),
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((search) => {
          if (!search) {
            recipient.autocompleteOptions = [];
            this.cdr.markForCheck();

            return of([] as RecipientDto[]);
          }

          recipient.isAutocompleteLoading = true;
          this.cdr.markForCheck();

          return this.recipientsApiService.getRecipients$(search).pipe(
            catchError(() => of([] as RecipientDto[])),
            finalize(() => {
              recipient.isAutocompleteLoading = false;
            }),
          );
        }),
        takeUntil(merge(this.formsReset$, recipient.destroy$)),
        untilDestroyed(this),
      )
      .subscribe((options) => {
        recipient.autocompleteOptions = options;
        this.syncResolvedSignatureRecipient(recipient, options);
        this.cdr.markForCheck();
      });
  }

  private uploadVersion(documentUniqueId: string, file: File): void {
    const nextVersionNumber = this.getNextVersionNumber();

    this.versionUploadInProgress = true;
    this.store$.dispatch(
      DocumentsActions.startUploadingDocumentVersion({
        data: {
          versionLabel: this.getVersionLabel(nextVersionNumber),
          documentName: this.document?.name || this.detailsTrigger?.documentName || file.name,
          fileName: file.name,
        },
      }),
    );

    this.fileUploadApiService
      .uploadFiles$([file])
      .pipe(
        untilDestroyed(this),
        map((uploadResponse) => uploadResponse.results?.[0]),
        switchMap((uploadedFile) => {
          if (
            !uploadedFile?.isSuccess ||
            !uploadedFile.fileId ||
            uploadedFile.validationErrors?.length
          ) {
            this.showVersionErrorNotification(
              this.getVersionUploadErrorMessage(file, uploadedFile),
            );

            return of(null);
          }

          return this.documentsApiService
            .createDocumentVersion$(documentUniqueId, { fileId: uploadedFile.fileId })
            .pipe(
              catchError(() => {
                this.showVersionErrorNotification(
                  this.translocoService.translate('text.versionAddError', {
                    name: file.name,
                  }),
                );

                return of(null);
              }),
            );
        }),
        finalize(() => {
          this.versionUploadInProgress = false;
          this.store$.dispatch(DocumentsActions.completeUploadingDocumentVersion());
          this.cdr.markForCheck();
        }),
      )
      .subscribe((createdVersion) => {
        if (!createdVersion) {
          return;
        }

        this.store$.dispatch(
          NotificationsActions.showNotificationComponent({
            icon: constants.icons.greenTick,
            message: this.translocoService.translate('text.versionAdded', {
              version: this.getVersionLabel(createdVersion.versionNumber || nextVersionNumber),
            }),
          }),
        );

        this.refreshVersionContext(documentUniqueId);
      });
  }

  private openVersion(version: DocumentVersionDto): void {
    this.resolveVersionDocument(version)
      .pipe(untilDestroyed(this))
      .subscribe((resolvedVersion: GetDocumentVersionDto | null) => {
        const viewerDocumentId = resolvedVersion?.uniqueId;

        if (viewerDocumentId) {
          window.open(`${environment.reviewServerUrl}/home?docGuid=${viewerDocumentId}`, '_blank');
          return;
        }

        this.showVersionErrorNotification(
          this.translocoService.translate('text.versionActionUnavailable'),
        );
      });
  }

  private openVersionInWordDesktop(version: DocumentVersionDto): void {
    if (this.isVersionWordDesktopDisabled(version)) {
      this.showVersionErrorNotification(
        this.translocoService.translate('text.versionActionUnavailable'),
      );
      return;
    }

    this.resolveVersionDocument(version)
      .pipe(untilDestroyed(this))
      .subscribe((resolvedVersion: GetDocumentVersionDto | null) => {
        const sharedLink = resolvedVersion?.sharedLink;

        if (sharedLink) {
          window.open(`ms-word:ofe|u|${sharedLink}`, '_blank');
          return;
        }

        this.showVersionErrorNotification(
          this.translocoService.translate('text.versionActionUnavailable'),
        );
      });
  }

  private downloadVersion(version: DocumentVersionDto): void {
    this.resolveVersionDocument(version)
      .pipe(untilDestroyed(this))
      .subscribe((resolvedVersion: GetDocumentVersionDto | null) => {
        if (resolvedVersion?.uniqueId) {
          this.store$.dispatch(
            DocumentsActions.downloadDocument({
              uniqueId: resolvedVersion.uniqueId,
              fileName: this.getVersionDocumentName(version, resolvedVersion),
            }),
          );
          return;
        }

        this.showVersionErrorNotification(
          this.translocoService.translate('text.versionActionUnavailable'),
        );
      });
  }

  private confirmDeleteVersion(version: DocumentVersionDto): void {
    const isOnlyAvailableVersion = this.versions.length === 1;

    this.store$.dispatch(
      DialogsActions.showDialog({
        data: {
          actionsKeys: ['yes', 'no'],
          content: isOnlyAvailableVersion
            ? this.translocoService.translate('modalBody.confirmDeleteOnlyDocumentVersionBody')
            : '',
          title: this.translocoService.translate(
            isOnlyAvailableVersion
              ? 'modalBody.confirmDeleteEntireDocumentFromVersion'
              : 'modalBody.confirmDeleteDocumentVersion',
          ),
        },
        callback: (action) => {
          if (action !== 'yes' || !this.detailsTrigger?.uniqueId) {
            return;
          }

          this.deleteVersion(this.detailsTrigger.uniqueId, version, isOnlyAvailableVersion);
        },
      }),
    );
  }

  private deleteVersion(
    documentUniqueId: string,
    version: DocumentVersionDto,
    isOnlyAvailableVersion: boolean,
  ): void {
    this.documentsApiService
      .deleteDocumentVersion$(documentUniqueId, version.versionNumber)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.store$.dispatch(
            NotificationsActions.showNotificationComponent({
              icon: constants.icons.greenTick,
              message: this.translocoService.translate('text.versionRemoved', {
                version: this.getVersionLabel(version.versionNumber),
              }),
            }),
          );

          if (isOnlyAvailableVersion) {
            this.closeAfterDeletingLastVersion();
            return;
          }

          this.refreshVersionContext(documentUniqueId);
        },
        error: () => {
          this.showVersionErrorNotification(
            this.translocoService.translate('text.versionRemoveError', {
              version: this.getVersionLabel(version.versionNumber),
            }),
          );
        },
      });
  }

  private closeAfterDeletingLastVersion(): void {
    this.store$.dispatch(DocumentsActions.getDocuments());
    this.closed.emit();
  }

  private refreshVersionContext(documentUniqueId: string): void {
    this.forceVersionContextRefresh = true;
    this.loadVersions(documentUniqueId);
    this.store$.dispatch(DocumentsActions.getDocuments());
    this.store$.dispatch(DocumentsActions.loadDocument({ uniqueId: documentUniqueId }));
  }

  private resolveVersionDocument(
    version: DocumentVersionDto,
  ): Observable<GetDocumentVersionDto | null> {
    if (!this.detailsTrigger?.uniqueId) {
      return of(null);
    }

    return this.documentsApiService
      .getDocumentVersion$(this.detailsTrigger.uniqueId, version.versionNumber)
      .pipe(
        map((resolvedVersion) => this.mergeVersionDocument(version, resolvedVersion)),
        catchError(() => of(this.mergeVersionDocument(version, null))),
      );
  }

  private mergeVersionDocument(
    version: DocumentVersionDto,
    resolvedVersion: GetDocumentVersionDto | null,
  ): GetDocumentVersionDto | null {
    const fallbackCurrentVersion = version.isCurrentVersion && this.document ? this.document : null;

    const mergedVersion = {
      ...(fallbackCurrentVersion || {}),
      ...(resolvedVersion || {}),
      ...version,
    } as GetDocumentVersionDto;

    if (!mergedVersion.uniqueId && !mergedVersion.directLink && !mergedVersion.sharedLink) {
      return null;
    }

    return mergedVersion;
  }

  private getVersionUploadErrorMessage(
    file: File,
    uploadedFile: FileUploadResultDto | undefined,
  ): string {
    if (uploadedFile?.validationErrors?.length) {
      return uploadedFile.validationErrors.join(' ');
    }

    if (uploadedFile?.errorMessage) {
      return uploadedFile.errorMessage;
    }

    return this.translocoService.translate('text.versionAddError', {
      name: file.name,
    });
  }

  private isAcceptedVersionFile(file: File): boolean {
    return constants.fileUploaderAcceptedFormatsTypes.includes(file.type);
  }

  private getNextVersionNumber(): number {
    return Math.max(0, ...this.versions.map((version) => version.versionNumber)) + 1;
  }

  private showVersionErrorNotification(message: string): void {
    this.store$.dispatch(
      NotificationsActions.showNotificationComponent({
        icon: constants.icons.redCross,
        message,
      }),
    );
  }

  private resetVersionsState(resetSearch: boolean = true): void {
    this.versions = [];
    this.versionTableRows = [];
    this.versionsLoading = false;
    this.versionUploadInProgress = false;
    this.currentVersionNumber = null;
    this.documentDetailsDrawerVersionsEventsService.setData([]);
    this.documentDetailsDrawerVersionsEventsService.onFilter([]);

    if (resetSearch) {
      this.versionSearchControl.reset('', { emitEvent: false });
    }

    this.scheduleVersionsTableRefresh();
  }

  private getVersionDocumentName(
    version: DocumentVersionDto,
    resolvedVersion: GetDocumentVersionDto | null,
  ): string {
    return (
      resolvedVersion?.documentName ||
      resolvedVersion?.name ||
      version.documentName ||
      this.document?.name ||
      'document.docx'
    );
  }

  private initVersionsSubscriptions(): void {
    this.versionSearchControl.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged())
      .subscribe((searchValue) => {
        this.documentDetailsDrawerVersionsEventsService.resetPagination();
        this.documentDetailsDrawerVersionsEventsService.onFilter(
          this.buildVersionFilters(`${searchValue || ''}`),
        );
      });

    this.documentDetailsDrawerVersionsEventsService
      .getSubject()
      .pipe(untilDestroyed(this))
      .subscribe((data: EventsServiceData) => {
        const version = this.versions.find((item) => `${item.versionNumber}` === `${data.id}`);

        if (!version) {
          return;
        }

        switch (data.action) {
          case EventsServiceAction.View: {
            this.onVersionAction(DocumentVersionAction.Open, version);
            break;
          }
          case EventsServiceAction.EditInDesktop: {
            this.onVersionAction(DocumentVersionAction.OpenInWordDesktop, version);
            break;
          }
          case EventsServiceAction.Download: {
            this.onVersionAction(DocumentVersionAction.Download, version);
            break;
          }
          case EventsServiceAction.Delete: {
            this.onVersionAction(DocumentVersionAction.Remove, version);
            break;
          }
        }
      });
  }

  private initVersionsTable(): void {
    this.versionMenuItems = [
      {
        title: this.translocoService.translate('actions.open'),
        event: dataTableEvents.common.view,
        icon: 'quick_reference_all',
      },
      {
        title: this.translocoService.translate('actions.openInWordDesktop'),
        event: dataTableEvents.common.editInDesktop,
        icon: 'quick_reference_all',
      },
      {
        title: this.translocoService.translate('actions.download'),
        event: dataTableEvents.common.download,
        icon: 'download',
      },
      {
        title: this.translocoService.translate('actions.remove'),
        event: dataTableEvents.common.delete,
        icon: 'delete',
      },
    ];

    this.versionColumns = [
      {
        title: this.translocoService.translate('columns.no'),
        data: 'versionNumber',
        width: '100px',
        createdCell: (cell: Node, cellData: any, rowData: DocumentVersionTableRow) => {
          this.decorateVersionRow(cell, rowData);
        },
        render: (data: number, type: string, full: DocumentVersionTableRow) => {
          return `<div class="document-details-drawer__version-label"><span>${this.getVersionLabel(data)}</span></div>`;
        },
      },
      {
        title: this.translocoService.translate('columns.added'),
        data: 'createdAt',
        createdCell: (cell: Node, cellData: any, rowData: DocumentVersionTableRow) => {
          this.decorateVersionRow(cell, rowData);
        },
        render: {
          display: (data: string) => this.formatVersionDate(data),
          sort: (data: string) => new Date(data).getTime(),
        },
      },
      {
        title: this.translocoService.translate('columns.addedBy'),
        data: 'createdByUserName',
        createdCell: (cell: Node, cellData: any, rowData: DocumentVersionTableRow) => {
          this.decorateVersionRow(cell, rowData);
        },
        render: (data: string) => data || '-',
      },
      {
        title: this.translocoService.translate('columns.name'),
        data: 'documentName',
        createdCell: (cell: Node, cellData: any, rowData: DocumentVersionTableRow) => {
          this.decorateVersionRow(cell, rowData);
        },
        render: (data: string) => data || '-',
      },
      {
        title: this.translocoService.translate('columns.actions'),
        width: '50px',
        data: 'versionKey',
        searchable: false,
        orderable: false,
        createdCell: (cell: Node, cellData: any, rowData: DocumentVersionTableRow) => {
          this.decorateVersionRow(cell, rowData);
        },
        render: (data: string, type: string, full: DocumentVersionTableRow) => {
          return this.threeDotsMenuPipe.transform(data, this.getVersionTableMenuItems(full));
        },
      },
    ];

    this.versionSort = [[0, 'desc']];
  }

  private getVersionTableMenuItems(version: DocumentVersionDto): DataTableItem[] {
    return this.versionMenuItems.map((item) => ({
      ...item,
      disabled:
        item.event === dataTableEvents.common.editInDesktop
          ? this.isVersionWordDesktopDisabled(version)
          : false,
    }));
  }

  private buildVersionTableRows(versions: DocumentVersionDto[]): DocumentVersionTableRow[] {
    return versions.map((version) => ({
      ...version,
      versionKey: `${version.versionNumber}`,
      searchText: [
        this.getVersionLabel(version.versionNumber),
        `${version.versionNumber}`,
        version.documentName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase(),
    }));
  }

  private buildVersionFilters(searchValue: string): SearchItemModel[] {
    const normalizedSearchValue = searchValue.trim();

    if (!normalizedSearchValue) {
      return [];
    }

    return [
      {
        criterion: DocumentVersionSearchCriterion.Search,
        operator: FILTER_OPERATOR.Contains,
        searchValue: normalizedSearchValue,
        type: SearchCriterionsDataType.String,
        controlName: 'versionSearch',
      },
    ];
  }

  private refreshVersionsTableData(): void {
    this.documentDetailsDrawerVersionsEventsService.setData(this.versionTableRows);
    this.documentDetailsDrawerVersionsEventsService.onFilter(
      this.buildVersionFilters(`${this.versionSearchControl.value || ''}`),
    );
    this.scheduleVersionsTableRefresh();
  }

  private scheduleVersionsTableRefresh(): void {
    setTimeout(() => {
      if (this.versionColumns) {
        this.refreshVersionsTable.next(this.versionColumns);
      }

      this.cdr.detectChanges();
    }, 1);
  }

  private decorateVersionRow(cell: Node, rowData: DocumentVersionTableRow): void {
    const rowElement = (cell as HTMLElement).parentElement;

    rowElement?.classList.toggle(
      'document-details-drawer__version-row--current',
      rowData.isCurrentVersion || this.currentVersionNumber === rowData.versionNumber,
    );
  }

  private buildPrepareDocumentSignatureRequest$(): Observable<PrepareDocumentSignatureRequest> {
    const recipients = this.buildSignatureRecipientSubmissions();
    const unresolvedRecipients = recipients.filter((recipient) => !recipient.recipientId);

    if (!unresolvedRecipients.length) {
      return of(this.buildPrepareDocumentSignaturePayload(recipients));
    }

    return this.recipientsApiService
      .resolveRecipients$({
        recipients: unresolvedRecipients.map((recipient) => ({
          name: recipient.name,
          email: recipient.email,
        })),
      })
      .pipe(
        map((resolvedRecipients) => {
          const resolvedRecipientsByKey = new Map(
            resolvedRecipients.map((recipient) => [
              this.buildSignatureRecipientLookupKey(recipient.name, recipient.email),
              recipient,
            ]),
          );

          unresolvedRecipients.forEach((recipient) => {
            const resolvedRecipient = resolvedRecipientsByKey.get(
              this.buildSignatureRecipientLookupKey(recipient.name, recipient.email),
            );

            if (!resolvedRecipient?.id) {
              throw new Error('SIGNATURE_RECIPIENT_RESOLVE_FAILED');
            }

            recipient.recipientId = resolvedRecipient.id;
            recipient.recipient.resolvedRecipient = resolvedRecipient;
            recipient.recipient.autocompleteOptions = this.mergeSignatureRecipientAutocompleteOptions(
              recipient.recipient.autocompleteOptions,
              [resolvedRecipient],
            );
          });

          return this.buildPrepareDocumentSignaturePayload(recipients);
        }),
      );
  }

  private buildPrepareDocumentSignaturePayload(
    recipients: SignatureRecipientSubmission[],
  ): PrepareDocumentSignatureRequest {
    return {
      recipients: recipients.map((recipient) => ({
        recipientId: recipient.recipientId || '',
        role: recipient.role,
      })),
    };
  }

  private buildSignatureRecipientSubmissions(): SignatureRecipientSubmission[] {
    return this.signatureRecipients.map((recipient) => {
      this.syncResolvedSignatureRecipient(recipient);

      return {
        recipient,
        recipientId: recipient.resolvedRecipient?.id || null,
        name: this.getSignatureRecipientName(recipient),
        email: this.getSignatureRecipientEmail(recipient),
        role: recipient.roleControl.value as DocumentSignatureRecipientRole,
      };
    });
  }

  private syncResolvedSignatureRecipient(
    recipient: DocumentSignatureRecipient,
    autocompleteOptions: RecipientDto[] = recipient.autocompleteOptions,
  ): void {
    const name = this.getSignatureRecipientName(recipient);
    const email = this.getSignatureRecipientEmail(recipient);

    if (
      recipient.resolvedRecipient &&
      this.buildSignatureRecipientLookupKey(recipient.resolvedRecipient.name, recipient.resolvedRecipient.email) !==
        this.buildSignatureRecipientLookupKey(name, email)
    ) {
      recipient.resolvedRecipient = null;
    }

    if (recipient.resolvedRecipient || !name || !email) {
      return;
    }

    recipient.resolvedRecipient =
      autocompleteOptions.find(
        (option) =>
          this.buildSignatureRecipientLookupKey(option.name, option.email) ===
          this.buildSignatureRecipientLookupKey(name, email),
      ) || null;
  }

  private buildSignatureRecipientLookupKey(name: string, email: string): string {
    return `${this.normalizeSignatureRecipientValue(name)}::${this.normalizeSignatureRecipientValue(email)}`;
  }

  private normalizeSignatureRecipientValue(value: string): string {
    return `${value || ''}`.trim().toLocaleLowerCase();
  }

  private mergeSignatureRecipientAutocompleteOptions(
    currentOptions: RecipientDto[],
    nextOptions: RecipientDto[],
  ): RecipientDto[] {
    const optionsByKey = new Map<string, RecipientDto>();

    [...currentOptions, ...nextOptions].forEach((option) => {
      optionsByKey.set(this.buildSignatureRecipientLookupKey(option.name, option.email), option);
    });

    return [...optionsByKey.values()];
  }

  private isDocusignViewportSupported(): boolean {
    return window.innerWidth >= this.docusignMinimumViewportWidth;
  }

  private isDocusignCompleteMessage(messageData: unknown): boolean {
    if (messageData === 'DOCUSIGN_COMPLETE') {
      return true;
    }

    if (typeof messageData === 'string') {
      try {
        const parsedMessage = JSON.parse(messageData) as { type?: string };

        return parsedMessage?.type === 'DOCUSIGN_COMPLETE';
      } catch {
        return false;
      }
    }

    return (messageData as { type?: string } | null)?.type === 'DOCUSIGN_COMPLETE';
  }

  private getDocusignIframeUrl(docusignUrl: string): string {
    try {
      const iframeUrl = new URL(docusignUrl);

      iframeUrl.searchParams.set('r', this.docusignReturnUrl);

      return iframeUrl.toString();
    } catch {
      return docusignUrl;
    }
  }

  private renderDocusignIframe(docusignUrl: string): void {
    this.docusignUrl = this.sanitizer.bypassSecurityTrustResourceUrl(docusignUrl);
    document.body.classList.add(this.docusignBodyClassName);
    this.cdr.markForCheck();
  }

  private completeDocusignRequest(): void {
    if (!this.docusignUrl) {
      return;
    }

    this.destroyDocusignIframe();
    this.showNotification(
      this.translocoService.translate('text.signatureRequestSentTitle'),
      this.translocoService.translate('text.signatureRequestSentText'),
      constants.icons.signature,
    );
    this.startSignatureSessionPolling();
  }

  private loadSignatureSession(documentId: string): void {
    this.documentSignatureApiService
      .getSignatureSession$(documentId)
      .pipe(
        catchError(() => EMPTY),
        takeUntil(this.formsReset$),
        untilDestroyed(this),
      )
      .subscribe((signatureSession) => {
        this.applySignatureSession(signatureSession, true);

        if (signatureSession?.status === DocumentSignatureSessionStatus.Created) {
          this.startSignatureSessionPolling(10000);
        }
      });
  }

  private startSignatureSessionPolling(initialDelayMs: number = 0): void {
    if (!this.document || this.isSignatureRequestCompleted) {
      return;
    }

    this.stopSignatureSessionPolling();

    timer(initialDelayMs, 10000)
      .pipe(
        exhaustMap(() =>
          this.documentSignatureApiService.getSignatureSession$(this.document!.uniqueId).pipe(
            catchError(() => EMPTY),
          ),
        ),
        takeUntil(merge(this.formsReset$, this.signatureSessionPollingStop$)),
        untilDestroyed(this),
      )
      .subscribe((signatureSession) => this.applySignatureSession(signatureSession));
  }

  private stopSignatureSessionPolling(): void {
    this.signatureSessionPollingStop$.next();
  }

  private applySignatureSession(
    signatureSession: DocumentSignatureSession | null,
    shouldReplaceRecipients: boolean = false,
  ): void {
    if (!signatureSession) {
      return;
    }

    this.signatureSessionId = signatureSession.id || signatureSession.envelopeId;
    this.signatureSessionStatus = signatureSession.status;
    this.isSignatureRequestPending = signatureSession.status === DocumentSignatureSessionStatus.Created;
    this.isSignatureRequestCompleted = signatureSession.status === DocumentSignatureSessionStatus.Completed;

    if (shouldReplaceRecipients) {
      this.signatureRecipients.forEach((recipient) => recipient.destroy$.next());
      this.nextSignatureRecipientId = 1;
      this.signatureRecipients = signatureSession.recipients.map((recipient) =>
        this.createSignatureRecipient(recipient),
      );
    }

    const recipientsByEmail = new Map(
      signatureSession.recipients.map((recipient) => [
        this.normalizeSignatureRecipientValue(recipient.email),
        recipient,
      ]),
    );

    this.signatureRecipients = this.signatureRecipients.map((recipient) => {
      const signatureRecipient = recipientsByEmail.get(
        this.normalizeSignatureRecipientValue(this.getSignatureRecipientEmail(recipient)),
      );

      return {
        ...recipient,
        completedAt:
          signatureRecipient?.status === DocumentSignatureSessionRecipientStatus.Completed
            ? signatureRecipient.statusChangedAt
            : recipient.completedAt,
      };
    });

    if (signatureSession.status === DocumentSignatureSessionStatus.Completed) {
      this.isSignatureRequestCompleted = true;
      this.stopSignatureSessionPolling();
    }

    this.cdr.markForCheck();
  }

  private destroyDocusignIframe(): void {
    this.docusignUrl = null;
    document.body.classList.remove(this.docusignBodyClassName);
    this.cdr.markForCheck();
  }

  private formatSignatureCompletionDate(value: string): string {
    const completedAt = new Date(value);

    if (Number.isNaN(completedAt.getTime())) {
      return value;
    }

    const day = `${completedAt.getDate()}`.padStart(2, '0');
    const month = `${completedAt.getMonth() + 1}`.padStart(2, '0');
    const year = completedAt.getFullYear();

    return `${day}/${month}/${year}`;
  }

  private getDocumentPreparationErrorTitle(error: HttpErrorResponse): string {
    return (
      error.error?.title || this.translocoService.translate('text.documentPreparationUnavailableTitle')
    );
  }

  private showNotification(title: string, message: string, icon = constants.icons.greenTick): void {
    this.store$.dispatch(
      NotificationsActions.showNotificationComponent({
        icon,
        title,
        message,
        duration: constants.notificationHideDelay,
      }),
    );
  }
}
